import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react'
import candidateChatManager from './CandidateChatManager'
import errorLogger from '../services/ErrorLogger'

const CandidateChatContext = createContext()

export const useCandidateChat = () => {
  const context = useContext(CandidateChatContext)
  if (!context) {
    throw new Error(
      'useCandidateChat must be used within a CandidateChatProvider',
    )
  }
  return context
}

export const CandidateChatProvider = ({ children, ...props }) => {
  // console.log('CandidateChatProvider - Initializing with props:', props)

  const [selectedCoach, setSelectedCoach] = useState(null)

  // Dark mode state with sessionStorage persistence
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const stored = sessionStorage.getItem('darkMode')
      return stored ? JSON.parse(stored) : false
    } catch (error) {
      console.warn('Error reading darkMode from sessionStorage:', error)
      return false
    }
  })

  // Check for chat_ended and call_missed URL parameters on initial load
  const getInitialView = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const hasChatEndedParam = urlParams.get('chat_ended') === '1'
    const hasCallMissedParam = urlParams.get('call_missed') === '1'

    // If URL has call_missed=1, show call_missed view regardless of connection state
    if (hasCallMissedParam) {
      return 'call_missed'
    }

    // If URL has chat_ended=1, show chat_ended view regardless of connection state
    if (hasChatEndedParam) {
      return 'chat_ended'
    }

    // If we have a pre-selected coach, start with chat view to prevent flash
    if (props.coachEasyRtcId) {
      return 'chat'
    }

    // Otherwise, start with select_coach view
    return 'select_coach'
  }

  const [currentView, setCurrentView] = useState(getInitialView)
  const initiatedCallRef = useRef(false)

  // Ref for message notification audio element
  const messageNotificationRef = useRef(null)

  // Add state for connection state to make it reactive
  const [connectionState, setConnectionState] = useState(
    candidateChatManager.getConnectionState(),
  )

  // Helper function to get message sound settings
  // Candidate side uses hardcoded values: Sound 1 (wet.mp3) at 70% volume
  const getMessageSoundSettings = useCallback(() => {
    return {
      url: 'https://yns-com.s3.amazonaws.com/audio/wet.mp3',
      volume: '0.7',
    }
  }, [])

  // Helper function to play message notification sound
  const playMessageNotification = useCallback(() => {
    const settings = getMessageSoundSettings()

    const audioElement = messageNotificationRef.current
    if (!audioElement) {
      return
    }

    // Set volume
    audioElement.volume = parseFloat(settings.volume)

    // Use the same approach as the old chat.js - click the element to simulate user interaction
    // This works around browser autoplay policies
    const playSound = () => {
      // Click the element to simulate user interaction (required for autoplay)
      audioElement.click()
      audioElement.play().catch((error) => {
        console.warn('Could not play message notification sound:', error)
      })
    }

    // Set source if it's different
    if (audioElement.src !== settings.url) {
      audioElement.src = settings.url
      audioElement.load()
    }

    // Play the sound
    if (audioElement.readyState >= 3) {
      playSound()
    } else {
      audioElement.oncanplaythrough = playSound
    }
  }, [getMessageSoundSettings])

  // Debug logging for selectedCoach changes
  useEffect(() => {
    // selectedCoach changed
  }, [selectedCoach])

  // Update currentView based on state
  useEffect(() => {
    const { chatStatus } = connectionState
    const urlParams = new URLSearchParams(window.location.search)
    const hasChatEndedParam = urlParams.get('chat_ended') === '1'
    const hasCallMissedParam = urlParams.get('call_missed') === '1'

    // useEffect triggered

    // If URL has call_missed=1, always show call_missed view regardless of chatStatus
    if (hasCallMissedParam) {
      if (currentView !== 'call_missed') {
        setCurrentView('call_missed')
      }
      return
    }

    // If URL has chat_ended=1, always show chat_ended view regardless of chatStatus
    if (hasChatEndedParam) {
      if (currentView !== 'chat_ended') {
        setCurrentView('chat_ended')
      }
      return
    }

    // Only continue with chatStatus-based logic if no URL params
    if (chatStatus === 'call_missed') {
      // Prevent multiple redirects by checking if we're already in the process of redirecting
      if (window.location.search.includes('call_missed=1')) {
        return
      }
      // Redirect to show call_missed view with proper URL parameters
      let url

      if (props.afterChatPath) {
        // Use the provided afterChatPath
        url = new URL(props.afterChatPath, window.location.origin)
      } else {
        // Fallback to current page
        url = new URL(window.location)
        // Clear all existing search params
        url.search = ''
      }

      if (!url.searchParams.has('call_missed')) {
        // Add new params
        url.searchParams.set('call_missed', '1')

        // Add conversation ID if available (try activeCall first, then lastChatId from localStorage)
        const conversationId =
          connectionState.activeCall?.conversationId ||
          connectionState.lastConversationId ||
          candidateChatManager.getLastChatId()
        if (conversationId) {
          url.searchParams.set('conversation_id', conversationId)
        }

        // Redirecting to call_missed
        // Perform the redirect
        window.location.href = url.toString()
      }
    } else if (chatStatus === 'chat_ended') {
      // Only redirect to chat_ended if chat was actually started
      // If chat wasn't started (e.g., candidate closed before coach answered), don't show rating
      if (connectionState.chatWasStarted === false) {
        // Chat was cancelled before starting, just go back to coach selection
        setCurrentView('select_coach')
        return
      }

      // Prevent multiple redirects by checking if we're already in the process of redirecting
      if (window.location.search.includes('chat_ended=1')) {
        return
      }
      // Redirect to show chat_ended view with proper URL parameters
      let url

      if (props.afterChatPath) {
        // Use the provided afterChatPath
        url = new URL(props.afterChatPath, window.location.origin)
      } else {
        // Fallback to current page
        url = new URL(window.location)
        // Clear all existing search params
        url.search = ''
      }

      if (!url.searchParams.has('chat_ended')) {
        // Add new params
        url.searchParams.set('chat_ended', '1')

        // Add conversation ID if available (try activeCall first, then lastChatId from localStorage)
        const conversationId =
          connectionState.activeCall?.conversationId ||
          connectionState.lastConversationId ||
          candidateChatManager.getLastChatId()
        if (conversationId) {
          url.searchParams.set('conversation_id', conversationId)
        }

        // Add who ended the chat
        if (connectionState.chatEndedBy) {
          url.searchParams.set('ended_by', connectionState.chatEndedBy)
        }

        // Add disconnection reason if this is a disconnection (not a normal chat end)
        if (
          connectionState.connectionLost &&
          connectionState.disconnectionReason
        ) {
          url.searchParams.set(
            'disconnection_reason',
            connectionState.disconnectionReason,
          )
        }

        // Redirecting to chat_ended
        // Perform the redirect
        window.location.href = url.toString()
      }
    } else if (chatStatus === 'call_cancelled') {
      // For cancelled calls, show call_cancelled view
      setCurrentView('call_cancelled')
    } else if (props.coachEasyRtcId) {
      setCurrentView('chat')
    } else {
      setCurrentView('select_coach')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.coachEasyRtcId,
    selectedCoach,
    connectionState.chatStatus,
    currentView,
  ])

  // Listen to state changes from ChatManager
  useEffect(() => {
    const handleStateChange = (_newState) => {
      // Get the computed connection state that includes chatStatus
      const computedState = candidateChatManager.getConnectionState()

      // Debug logging

      // If we have a chatMode from props and it's not set in the state, use the prop value
      if (
        props.chatMode &&
        props.chatMode !== 'text' &&
        !computedState.chatMode
      ) {
        computedState.chatMode = props.chatMode
      }

      // Update the local state with the computed state
      setConnectionState(computedState)
    }

    const handleVideoLoadingStarted = (data) => {
      // Update the connection state to show video loading
      setConnectionState((prev) => ({
        ...prev,
        chatStatus: 'obtaining_video',
        chatMode: data.chatMode,
      }))
    }

    const handleMediaSourceInitFailed = (_data) => {
      // Update the connection state to show media failure
      setConnectionState((prev) => ({
        ...prev,
        chatStatus: 'media_failed',
      }))
    }

    const handleAudioMuteChanged = (data) => {
      // Update the connection state with the new audio mute state
      setConnectionState((prev) => ({
        ...prev,
        audioMuted: data.muted,
      }))
    }

    const handleMessageReceived = (data) => {
      // Play message notification sound only for messages from coach (not from candidate)
      if (data.message?.sender === 'coach') {
        playMessageNotification()
      }
    }

    // Add event listeners
    candidateChatManager.addEventListener('stateChange', handleStateChange)
    candidateChatManager.addEventListener(
      'videoLoadingStarted',
      handleVideoLoadingStarted,
    )
    candidateChatManager.addEventListener(
      'mediaSourceInitFailed',
      handleMediaSourceInitFailed,
    )
    candidateChatManager.addEventListener(
      'audioMuteChanged',
      handleAudioMuteChanged,
    )
    candidateChatManager.addEventListener('peerMessage', handleMessageReceived)

    // Initial state setup
    const initialState = candidateChatManager.getConnectionState()

    // If we have a chatMode from props, set it in the ChatManager
    if (props.chatMode && props.chatMode !== 'text') {
      // Set the chat mode in the ChatManager's connection state
      if (candidateChatManager.connectionState) {
        candidateChatManager.connectionState.chatMode = props.chatMode
      }

      // If this is a video call, trigger the video initialization
      if (props.chatMode === 'video') {
        // The ChatManager will handle this when initiateCall is called
      }
    }

    setConnectionState(initialState)

    return () => {
      candidateChatManager.removeEventListener('stateChange', handleStateChange)
      candidateChatManager.removeEventListener(
        'videoLoadingStarted',
        handleVideoLoadingStarted,
      )
      candidateChatManager.removeEventListener(
        'mediaSourceInitFailed',
        handleMediaSourceInitFailed,
      )
      candidateChatManager.removeEventListener(
        'audioMuteChanged',
        handleAudioMuteChanged,
      )
      candidateChatManager.removeEventListener(
        'peerMessage',
        handleMessageReceived,
      )
    }
  }, [props.chatMode, playMessageNotification])

  // Initialize error logger
  useEffect(() => {
    if (props.log_chat_event_path) {
      errorLogger.initialize(
        candidateChatManager.logEvent.bind(candidateChatManager),
        props.conversationId,
      )
    }

    // Update conversation ID when it changes
    if (props.conversationId) {
      errorLogger.setConversationId(props.conversationId)
    }

    return () => {
      errorLogger.cleanup()
    }
  }, [props.log_chat_event_path, props.conversationId])

  useEffect(() => {
    // Reset the initiated call flag when coachEasyRtcId changes
    initiatedCallRef.current = false

    // Only reset the selected coach if we don't have a coachEasyRtcId
    // This prevents clearing the coach when we're trying to connect to a specific coach
    if (!props.coachEasyRtcId) {
      setSelectedCoach(null)
    }

    // Mirror CoachSelectionList: never connect/initiateCall on survey or
    // missed-call routes (CC-UX-027 — refresh must not phantom-ring the coach).
    const urlParams = new URLSearchParams(window.location.search)
    const isChatEnded =
      urlParams.get('chat_ended') === '1' ||
      urlParams.get('call_missed') === '1'

    if (isChatEnded) {
      if (candidateChatManager.getConnectionState()?.isConnected) {
        candidateChatManager.disconnect()
      }
      return
    }

    // Set up event listeners for auto-coach selection
    const handleCoachesUpdate = (coaches) => {
      /* console.log(
            'CandidateChatContext - handleCoachesUpdate called with coaches:',
            coaches,
          ) */

      // Check dynamically if we're in chat_ended or call_missed view
      // This ensures we check the current URL state, not the closure value
      const currentUrlParams = new URLSearchParams(window.location.search)
      const isCurrentlyChatEnded =
        currentUrlParams.get('chat_ended') === '1' ||
        currentUrlParams.get('call_missed') === '1'

      // Update the connectionState with the new coaches data
      setConnectionState((prevState) => ({
        ...prevState,
        coaches: coaches,
      }))

      // If we have a specific coach ID, only process updates that involve that coach
      if (props.coachEasyRtcId) {
        const hasRelevantCoach = coaches.some(
          (coach) => coach.easyrtcid === props.coachEasyRtcId,
        )

        if (!hasRelevantCoach) {
          /*
          console.log(
            'CandidateChatContext - No relevant coach found, returning',
          )
          */
          return
        }
      }

      // Auto-select coach if specified and no call is currently active
      const currentConnectionState = candidateChatManager.getConnectionState()

      // Don't initiate call if chat has ended, call was missed, or call was cancelled
      const chatStatus = currentConnectionState.chatStatus
      const isChatEndedOrMissed =
        isCurrentlyChatEnded ||
        chatStatus === 'chat_ended' ||
        chatStatus === 'call_missed' ||
        chatStatus === 'call_cancelled'

      if (
        props.coachEasyRtcId &&
        !currentConnectionState.activeCall &&
        !initiatedCallRef.current &&
        !isChatEndedOrMissed
      ) {
        /*
      // Auto-selecting coach
        */

        // Try to find coach by easyrtcid first, then by username as fallback
        let specifiedCoach = coaches.find(
          (coach) => coach.easyrtcid === props.coachEasyRtcId,
        )

        // If not found by easyrtcid, try to find by username
        if (!specifiedCoach) {
          specifiedCoach = coaches.find(
            (coach) => coach.username === props.coachEasyRtcId,
          )
        }

        if (specifiedCoach) {
          setSelectedCoach(specifiedCoach)
          // Also update the manager's connection state
          candidateChatManager.setSelectedCoach(specifiedCoach)
          initiatedCallRef.current = true
          // initiateCall re-checks EasyRTC presence and aborts if coach is away (CTS-9831)
          candidateChatManager.initiateCall(
            specifiedCoach.easyrtcid,
            props.chatMode || 'text',
            props.conversationId,
          )
        } else {
          // Specified coach not found in coaches list
        }
      } else {
        /*
        console.log('CandidateChatContext - Not auto-selecting coach:', {
          hasCoachEasyRtcId: !!props.coachEasyRtcId,
          hasActiveCall: !!currentConnectionState.activeCall,
          initiatedCallRef: initiatedCallRef.current,
        })
        */
      }
    }

    // Add event listener for coaches updates
    candidateChatManager.addEventListener('coachesUpdate', handleCoachesUpdate)

    if (props.coachEasyRtcId) {
      candidateChatManager
        .connect(props.socketUrl, props.username, props.chatRoom, {
          userMode: 'candidate',
          socketUrl: props.socketUrl,
          username: props.username,
          chatRoom: props.chatRoom,
          inHours: props.inHours,
          logChatEventPath: props.log_chat_event_path,
          logChatMissedOpportunityPath: props.log_chat_missed_opportunity_path,
          chatCoachInfoPath: props.chatCoachInfoPath,
          serverTime: props.serverTime,
          currentUserLocale: props.current_user?.locale,
          displayName: props.userDisplayName || props.username,
          firstName: props.userFirstName,
          videoEnabled: props.videoEnabled || false,
          browser: props.browserName || 'unknown',
          userLocale: props.userLocale || 'en',
          userLanguage: props.userLanguage || 'English',
          userCompany: props.userCompany,
          userCompanyId: props.userCompanyId,
          partnerCandidate: props.partnerCandidate || false,
          conversationStarter: props.conversationStarter,
          chatMode: props.chatMode || 'text',
          coachEasyRtcId: props.coachEasyRtcId,
          userId: props.userId,
          userEmail: props.userEmail,
          avatar: props.avatar,
          os: props.os,
          buildChatHistoryPath: props.buildChatHistoryPath,
          conversationId: props.conversationId,
          isMobile: props.isMobile || false,
          clientTime: props.clientTime,
          timeOffset: props.timeOffset,
          deloitteUser: props.deloitteUser,
          userCareerDevelopment: props.userCareerDevelopment,
          outplacement: props.outplacement,
          opRedesign: props.opRedesign,
          enableAudio: false,
          enableVideo: false,
          enableDataChannels: false,
          autoInitUserMedia: false,
          debug: false,
          updateIntervalMs: 5000,
          appName: 'yns.coach_chat',
          presenceMessage: 'Candidate Chat',
          chatHours: props.chatHours,
          hideChatHours: props.hideChatHours || false,
          modalCopy: props.modalCopy || {},
        })
        .catch(console.error)
    } else {
      setCurrentView('select_coach')
    }

    // Cleanup
    return () => {
      candidateChatManager.removeEventListener(
        'coachesUpdate',
        handleCoachesUpdate,
      )
    }
  }, [
    props.coachEasyRtcId,
    props.avatar,
    props.browserName,
    props.buildChatHistoryPath,
    props.chatMode,
    props.chatRoom,
    props.clientTime,
    props.conversationId,
    props.conversationStarter,
    props.current_user?.locale,
    props.deloitteUser,
    props.inHours,
    props.isMobile,
    props.log_chat_event_path,
    props.log_chat_missed_opportunity_path,
    props.chatCoachInfoPath,
    props.opRedesign,
    props.outplacement,
    props.os,
    props.partnerCandidate,
    props.serverTime,
    props.socketUrl,
    props.timeOffset,
    props.userCareerDevelopment,
    props.userCompany,
    props.userCompanyId,
    props.userDisplayName,
    props.userEmail,
    props.userFirstName,
    props.userId,
    props.userLanguage,
    props.userLocale,
    props.username,
    props.videoEnabled,
    props.chatHours,
    props.hideChatHours,
    props.modalCopy,
  ]) // Removed selectedCoach from dependency array; props object itself excluded (new ref each render)

  // Helper function to get human-readable chat status
  const getChatStatusMessage = () => {
    const state = candidateChatManager.getConnectionState()
    switch (state.chatStatus) {
      case 'disconnected':
        return 'Disconnected'
      case 'connecting':
        return 'Connecting to chat...'
      case 'waiting_for_coach':
        return state.activeCall?.status === 'ringing'
          ? 'Calling coach...'
          : 'Waiting for coach to accept...'
      case 'chat_active':
        return 'Chat active with coach'
      case 'chat_ended':
        return 'Chat ended'
      default:
        return 'Unknown status'
    }
  }

  // Dark mode helper functions
  const getDarkModeClasses = (lightClasses, darkClasses) => {
    return isDarkMode ? darkClasses : lightClasses
  }

  const getBackgroundClass = () => {
    return getDarkModeClasses('bg-white', 'bg-gray-800')
  }

  const getTextClass = () => {
    return getDarkModeClasses('text-gray-900', 'text-white')
  }

  const getInputClass = () => {
    return getDarkModeClasses(
      'border-neutral-300 bg-slate-50 text-gray-900 placeholder:text-gray-500 focus:border-green-800 focus:ring-green-800',
      'border-gray-600 bg-gray-700 text-white placeholder:text-gray-400 focus:border-green-400 focus:ring-green-400',
    )
  }

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => {
      const newValue = !prev
      try {
        sessionStorage.setItem('darkMode', JSON.stringify(newValue))
      } catch (error) {
        console.warn('Error saving darkMode to sessionStorage:', error)
      }
      return newValue
    })
  }

  const contextValue = {
    // Provide direct access to chatManager
    candidateChatManager,

    // Current connection state (reactive)
    connectionState,

    // UI state
    currentView,

    // Coach selection
    selectedCoach,

    // Dark mode state and helpers
    isDarkMode,
    toggleDarkMode,
    getDarkModeClasses,
    getBackgroundClass,
    getTextClass,
    getInputClass,

    // Props passed from parent
    candidateCoachChatSurveyPath: props.candidateCoachChatSurveyPath,
    afterChatPath: props.afterChatPath,

    // Helper functions
    getChatStatusMessage,

    // Chat actions
    setSelectedCoach: (coach) => {
      setSelectedCoach(coach)
      // Also update the manager's connection state
      candidateChatManager.setSelectedCoach(coach)
    },
    initiateCall: (coachEasyrtcid) => {
      return candidateChatManager.initiateCall(
        coachEasyrtcid,
        props.chatMode || 'text',
        props.conversationId,
      )
    },
    sendMessage: (content) => {
      return candidateChatManager.sendMessage(content)
    },
    sendTypingIndicator: () => {
      return candidateChatManager.sendTypingIndicator()
    },

    // All the props for easy access
    ...props,

    // Add camelCase versions for components that expect them
    logChatEventPath: props.log_chat_event_path,
  }

  return (
    <CandidateChatContext.Provider value={contextValue}>
      {children}
      {/* Hidden audio element for message notifications */}
      <audio ref={messageNotificationRef} preload="auto" />
    </CandidateChatContext.Provider>
  )
}
